import useReveal from "@/hooks/useReveal";
import { aiProducts } from "@/lib/home-content";
import { scrollToId } from "@/lib/scroll";

const STATUS = {
    beta: {
        label: "Beta",
        dot: "bg-[#7aa2ff]",
        pill: "text-[#7aa2ff]",
    },
    live: {
        label: "Live",
        dot: "bg-[#16a34a]/55",
        pill: "text-[#3d8f62]",
    },
    development: {
        label: "In development",
        dot: "bg-[#71717a]/50",
        pill: "text-[#71717a]",
    },
};

function StatusBadge({ status }) {
    const tone = STATUS[status] || STATUS.live;
    return (
        <span
            className={`inline-flex shrink-0 items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.12em] ${tone.pill}`}
        >
            <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
            {tone.label}
        </span>
    );
}

function ProductCard({ product, index }) {
    const { ref, visible } = useReveal({ threshold: 0.08 });

    return (
        <article
            ref={ref}
            style={{ transitionDelay: `${(index % 3) * 70}ms` }}
            className={`reveal flex h-full flex-col rounded-2xl bg-white p-6 ring-1 ring-black/5 ${
                visible ? "is-visible" : ""
            }`}
        >
            <StatusBadge status={product.status} />
            <h3 className="mt-5 font-display text-[1.35rem] font-bold leading-tight text-[#0a0a0b]">{product.name}</h3>
            <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8a8a95]">
                {product.category}
            </p>
            <p className="mt-3 flex-1 text-sm leading-relaxed text-[#5b5b66]">{product.description}</p>
            <a
                href={product.href}
                {...(product.href.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
                className="mt-5 inline-flex w-fit items-center gap-1 border-b border-[#0a0a0b] pb-0.5 text-sm font-medium text-[#0a0a0b] transition-colors hover:border-[var(--brand)] hover:text-[var(--brand)]"
            >
                View Live Product
                <span aria-hidden="true">→</span>
            </a>
        </article>
    );
}

export default function AiProducts() {
    const { ref, visible } = useReveal();

    return (
        <section id="ai-products" data-testid="ai-products-section" className="bg-[#f6f7fb] px-5 py-16 sm:py-20">
            <div className="mx-auto max-w-6xl">
                <div ref={ref} className={`reveal max-w-3xl ${visible ? "is-visible" : ""}`}>
                    <span className="section-kicker">{aiProducts.kicker}</span>
                    <h2 className="section-title">{aiProducts.title}</h2>
                    <p className="section-lead">{aiProducts.subtitle}</p>
                </div>

                <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {aiProducts.items.map((product, index) => (
                        <ProductCard key={product.name} product={product} index={index} />
                    ))}
                </div>

                <div className="mt-12 flex justify-center">
                    <button
                        type="button"
                        data-testid="ai-products-discovery-call"
                        onClick={() => scrollToId("#contact")}
                        className="rounded-full bg-[var(--brand)] px-6 py-3 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(43,80,255,0.35)] transition-transform hover:-translate-y-0.5"
                    >
                        {aiProducts.cta}
                    </button>
                </div>
            </div>
        </section>
    );
}
